import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import apiClient from '../api/client';
import { getAllUsers } from '../api/admin';
import { useAuth } from './AuthContext';
import type { IO } from '../types/io';
import type { GL, Vendor, Area, CostCenter } from '../types/admin';
import type { User } from '../types/user';
import { decodeGL, decodeIO, decodeVendor } from '../utils/decodeApiStrings';

interface AppDataContextValue {
  allGLs: GL[];
  allVendors: Vendor[];
  allUsers: User[];
  allIOs: IO[];
  allAreas: Area[];
  allCostCenters: CostCenter[];
  isLoading: boolean;
  refreshGLs: () => Promise<void>;
  refreshVendors: () => Promise<void>;
  refreshUsers: () => Promise<void>;
  refreshIOs: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, currentUser } = useAuth();
  const [allGLs, setAllGLs] = useState<GL[]>([]);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allIOs, setAllIOs] = useState<IO[]>([]);
  const [allAreas] = useState<Area[]>([]);
  const [allCostCenters] = useState<CostCenter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // In-flight promise refs — concurrent callers share one fetch instead of
  // each firing their own paginated waterfall.
  const usersInflightRef = useRef<Promise<void> | null>(null);
  const iosInflightRef = useRef<Promise<void> | null>(null);

  const refreshGLs = useCallback(async () => {
    try {
      const { data } = await apiClient.get<GL[]>('/gls');
      setAllGLs(data.map(decodeGL));
    } catch (err) {
      console.error('Failed to load GLs:', err);
    }
  }, []);

  const refreshVendors = useCallback(async () => {
    try {
      const { data } = await apiClient.get<Vendor[]>('/vendors');
      setAllVendors(data.map(decodeVendor));
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  }, []);

  const refreshUsers = useCallback((): Promise<void> => {
    if (usersInflightRef.current) return usersInflightRef.current;
    const p = getAllUsers()
      .then((users) => { setAllUsers(users); })
      .catch((err) => { console.error('Failed to load users:', err); })
      .finally(() => { usersInflightRef.current = null; });
    usersInflightRef.current = p;
    return p;
  }, []);

  const refreshIOs = useCallback((): Promise<void> => {
    if (iosInflightRef.current) return iosInflightRef.current;
    const p = apiClient.get<IO[]>('/ios')
      .then(({ data }) => { setAllIOs(data.map(decodeIO)); })
      .catch((err) => { console.error('Failed to load IOs:', err); })
      .finally(() => { iosInflightRef.current = null; });
    iosInflightRef.current = p;
    return p;
  }, []);

  // Load lookup data after authentication
  useEffect(() => {
    if (!isAuthenticated) return;

    setIsLoading(true);

    // Only load lightweight lookups at startup (GLs, Vendors).
    // Users and IOs are loaded on demand to avoid slow queries over remote RDS.
    const loads: Promise<void>[] = [refreshGLs(), refreshVendors()];

    Promise.allSettled(loads).finally(() => setIsLoading(false));
  }, [isAuthenticated, refreshGLs, refreshVendors]);

  return (
    <AppDataContext.Provider
      value={{
        allGLs, allVendors, allUsers, allIOs, allAreas, allCostCenters,
        isLoading, refreshGLs, refreshVendors, refreshUsers, refreshIOs,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData(): AppDataContextValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}
